import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Input,
  Select,
  Spin,
  Tabs,
  Modal,
  Popconfirm,
  notification,
  message,
} from "antd";
import {
  DownloadOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  SearchOutlined,
  InboxOutlined,
  PlusOutlined,
  LoadingOutlined,
  HourglassOutlined,
  WarningOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatAppNumber } from "util/locale";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiRequest } from "util/services";
import AiAutoRefreshSettings from "../AutoRefreshSettings";
import { formatInUserTimezone } from "util/formatInUserTimezone";
import dayjs from "dayjs";
import styles from "./style.module.scss";
import { ReportCardOverflowMenu, RegenerateAllConfirmContent } from "./ReportCardOverflowMenu";
import {
  formatAiReportCoverageLabel,
  formatAiReportPeriodLabel,
  formatWidgetBreakdownLabel,
  isOneTimeAiReport,
  reportDisplayStatus,
  reportHasActiveWidgetJobs,
  reportMetricChipTone,
  reportMetricStatus,
  withIncompleteMetricsInProgress,
} from "util/aiReportWidgetBreakdown";

const { Option } = Select;

const STATUS_TABS = ["all", "ready", "processing", "failed"];
// statuses that map to the "processing" tab (sent to backend as separate filters)
const PROCESSING_TAB_STATUSES = ["processing", "provisioned", "finalizing"];

function canOpenAiReportDetail(report) {
  if (!report?.welcome_group_id) return false;
  return ["ready", "finalizing", "provisioned", "partial", "insufficient_data"].includes(
    reportDisplayStatus(report),
  );
}

/** Same names as My Workspace / AiReport detail (`welcome.metricName_*`). */
function reportMetricChipLabel(metricId, t) {
  const id = Number(metricId);
  if (!Number.isFinite(id) || id < 1) {
    return metricId != null && metricId !== "" ? String(metricId) : "";
  }
  const key = `welcome.metricName_${id}`;
  const label = t(key);
  return label !== key ? label : `Metric ${id}`;
}

function renderMetricStatusChip(report, mid, t, { forceIncompleteInProgress = false } = {}) {
  let status = reportMetricStatus(report, mid);
  if (forceIncompleteInProgress && reportMetricChipTone(status) === "problem") {
    status = "in_progress";
  }
  const tone = reportMetricChipTone(status);
  const chipClass = [
    styles.metricChip,
    tone === "ready" ? styles.metricChipReady : null,
    tone === "problem" ? styles.metricChipProblem : null,
    tone === "inProgress" ? styles.metricChipInProgress : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <span key={mid} className={chipClass}>
      <span className={styles.metricChipLabel}>{reportMetricChipLabel(mid, t)}</span>
      {tone === "ready" ? (
        <CheckOutlined className={styles.metricChipIconReady} aria-hidden />
      ) : null}
      {tone === "problem" ? (
        <CloseOutlined className={styles.metricChipIconProblem} aria-hidden />
      ) : null}
      {tone === "inProgress" ? (
        <HourglassOutlined className={styles.metricChipIconProgress} aria-hidden />
      ) : null}
    </span>
  );
}

function reportDashboardContextPrimary(report, sessionDashboardName) {
  const session = String(sessionDashboardName ?? "").trim();
  const ws = String(report?.welcome_group_name ?? "").trim();
  if (session && ws) return `${session} · ${ws}`;
  if (session) return session;
  if (ws) return ws;
  return "";
}

function reportDashboardContextTitle(report, sessionDashboardName, t) {
  const session = String(sessionDashboardName ?? "").trim();
  const ws = String(report?.welcome_group_name ?? "").trim();
  if (!session && !ws) return t("ai_services.reports_session_dashboard_none");
  if (!session && ws) return t("ai_services.reports_workspace_group_hint");
  return undefined;
}

const POLL_INTERVAL_MS = 8000;

const SORT_OPTIONS = [
  { value: "created_at:desc", sortBy: "created_at", sortOrder: "desc" },
  { value: "created_at:asc", sortBy: "created_at", sortOrder: "asc" },
  { value: "name:asc", sortBy: "name", sortOrder: "asc" },
  { value: "name:desc", sortBy: "name", sortOrder: "desc" },
];

const AiReportsPage = ({ user }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  // Report Settings is a panel within this same route (?view=settings), reached
  // from the inline gear on the Scheduled Reports nav row — not a separate page.
  const pageView =
    new URLSearchParams(location.search).get("view") === "settings"
      ? "settings"
      : "reports";
  const pageViewTabItems = [
    { key: "reports", label: t("ai_services.reports_title") },
    { key: "settings", label: t("ai_services.auto_refresh_title") },
  ];
  const handlePageViewChange = (key) => {
    history.push(
      key === "settings"
        ? "/console/ai-services/auto-reports?view=settings"
        : "/console/ai-services/auto-reports",
    );
  };
  const userReducer = useSelector((state) => state.userReducer);
  const sessionDashboardName = String(
    useSelector((state) => state.DashboardReducer?.dashboard?.Name) ?? "",
  ).trim();

  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [activeTab, setActiveTab] = useState("all");
  const [allReportTypeTab, setAllReportTypeTab] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [viewMode, setViewMode] = useState("card");
  const [sortValue, setSortValue] = useState("created_at:desc");
  const debounceRef = useRef(null);

  const pollRef = useRef(null);
  /** For tab/filter refetch: use latest length so we pick silent loading vs full-page spin. */
  const reportsLenRef = useRef(0);
  /** Keep page out of fetchReports deps so load-more does not retrigger the filter reset effect. */
  const pageRef = useRef(1);

  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [regeneratingReportIds, setRegeneratingReportIds] = useState([]);
  const [detailReport, setDetailReport] = useState(null);
  /** From GET /ai-services/reports — Users.setting_timezone via API (DB). */
  const [userTimezoneLabel, setUserTimezoneLabel] = useState(null);

  const PAGE_SIZE = 12;

  const activeSort = SORT_OPTIONS.find((option) => option.value === sortValue)
    || SORT_OPTIONS[0];

  const fetchReports = useCallback(
    async (reset = false, forcePage = null, silent = false) => {
      const newPage = reset ? 1 : (forcePage ?? pageRef.current);
      if (reset) {
        setPage(1);
        pageRef.current = 1;
      } else if (forcePage != null) {
        pageRef.current = forcePage;
      }
      if (silent) {
        setTabLoading(true);
      } else {
        setLoading(true);
      }
      try {
        const params = new URLSearchParams({
          page: newPage,
          page_size: PAGE_SIZE,
          sort_by: activeSort.sortBy,
          sort_order: activeSort.sortOrder,
        });
        if (activeTab === "processing") {
          params.set("status", PROCESSING_TAB_STATUSES.join(","));
        } else if (activeTab !== "all") {
          params.set("status", activeTab);
        }
        if (allReportTypeTab !== "all") {
          params.set("report_type", allReportTypeTab);
        }
        if (monthFilter) params.set("month", monthFilter);
        if (brandSearch.trim()) params.set("brand_search", brandSearch.trim());

        // getFullResult=true: never collapse API errors to `false` so we can surface them.
        const res = await apiRequest(
          `welcome/ai-services/reports?${params.toString()}`,
          "GET",
          null,
          true,
        );
        if (!res || (res.error && !res.success)) {
          notification.error({
            message: t("ai_services.load_error"),
            description:
              typeof res?.error === "string" ? res.error : res?.msg || undefined,
          });
          if (reset) {
            setReports([]);
            setTotal(0);
          }
          return;
        }
        if (res.success) {
          const incoming = res.success.reports || [];
          if (res.success.user_timezone_label) {
            setUserTimezoneLabel(res.success.user_timezone_label);
          }
          setTotal(res.success.total || 0);
          if (reset) {
            setReports(incoming);
            setHasMore(incoming.length < (res.success.total || 0));
          } else {
            setReports((prev) => {
              const next = [...prev, ...incoming];
              setHasMore(next.length < (res.success.total || 0));
              return next;
            });
          }
        }
      } catch (e) {
        notification.error({ message: t("ai_services.load_error") });
      } finally {
        setLoading(false);
        setTabLoading(false);
      }
    },
    [
      activeTab,
      allReportTypeTab,
      monthFilter,
      brandSearch,
      activeSort.sortBy,
      activeSort.sortOrder,
    ]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setBrandSearch(brandSearchInput);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [brandSearchInput]);

  useEffect(() => {
    reportsLenRef.current = reports.length;
  }, [reports.length]);

  useEffect(() => {
    fetchReports(true, null, reportsLenRef.current > 0);
  }, [activeTab, allReportTypeTab, monthFilter, brandSearch, sortValue, fetchReports]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    pageRef.current = nextPage;
    fetchReports(false, nextPage);
  };

  const handleDownload = async (report) => {
    if (!report.pdf_url) {
      message.warning(t("ai_services.no_pdf_yet"));
      return;
    }
    setDownloadingId(report.id);
    try {
      window.open(report.pdf_url, "_blank");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (report) => {
    setDeletingId(report.id);
    try {
      const res = await apiRequest(
        `welcome/ai-services/reports/${report.id}`,
        "DELETE"
      );
      if (res?.success) {
        message.success(t("ai_services.report_deleted"));
        setReports((prev) => prev.filter((r) => r.id !== report.id));
        setTotal((prev) => Math.max(0, prev - 1));
      } else {
        message.error(t("ai_services.delete_failed"));
      }
    } catch {
      message.error(t("ai_services.delete_failed"));
    } finally {
      setDeletingId(null);
    }
  };

  const markReportRegenerating = (reportId, regenerating) => {
    setRegeneratingReportIds((prev) => {
      if (regenerating) {
        return prev.includes(reportId) ? prev : [...prev, reportId];
      }
      return prev.filter((id) => id !== reportId);
    });
  };

  const refreshReportAfterRegen = async (
    report,
    payload,
    { markIncompleteInProgress = false } = {},
  ) => {
    const newStatus = String(payload?.status || "provisioned").toLowerCase();
    setReports((prev) =>
      prev.map((r) => {
        if (r.id !== report.id) return r;
        let next = { ...r, status: newStatus, display_status: newStatus };
        if (markIncompleteInProgress) {
          next = withIncompleteMetricsInProgress(next);
        }
        return next;
      }),
    );
    await fetchReports(true, null, true);
    if (markIncompleteInProgress) {
      setReports((prev) =>
        prev.map((r) => {
          if (r.id !== report.id) return r;
          if ((Number(r.widgets_in_progress) || 0) > 0) return r;
          return withIncompleteMetricsInProgress(r);
        }),
      );
    }
  };

  const handleRegeneratePdf = async (report) => {
    markReportRegenerating(report.id, true);
    try {
      const res = await apiRequest(
        `welcome/ai-services/reports/${report.id}/regenerate-pdf`,
        "POST",
        {},
      );
      if (res?.success) {
        message.success(t("ai_services.regenerate_pdf_only_started"));
        await refreshReportAfterRegen(report, { status: "finalizing" });
      } else {
        message.error(
          typeof res?.error === "string" ? res.error : t("ai_services.save_error"),
        );
      }
    } catch {
      message.error(t("ai_services.save_error"));
    } finally {
      markReportRegenerating(report.id, false);
    }
  };

  const handleRepairWidgets = async (report) => {
    markReportRegenerating(report.id, true);
    setReports((prev) =>
      prev.map((r) =>
        r.id === report.id
          ? withIncompleteMetricsInProgress({
              ...r,
              status: "provisioned",
              display_status: "provisioned",
            })
          : r,
      ),
    );
    try {
      const res = await apiRequest(
        `welcome/ai-services/reports/${report.id}/regenerate`,
        "POST",
        { mode: "repair" },
      );
      if (res?.success) {
        message.success(t("ai_services.regenerate_repair_started"));
        await refreshReportAfterRegen(report, res.success, {
          markIncompleteInProgress: true,
        });
      } else if (res?.error === "NOTHING_TO_REPAIR") {
        message.info(t("ai_services.regenerate_repair_nothing"));
        await fetchReports(true, null, true);
      } else {
        message.error(
          typeof res?.error === "string" ? res.error : t("ai_services.save_error"),
        );
        await fetchReports(true, null, true);
      }
    } catch {
      message.error(t("ai_services.save_error"));
      await fetchReports(true, null, true);
    } finally {
      markReportRegenerating(report.id, false);
    }
  };

  const handleRegenerateAll = async (report) => {
    const hide = message.loading(t("ai_services.regenerate_all_preview_loading"), 0);
    let preview = null;
    try {
      const previewRes = await apiRequest(
        `welcome/ai-services/reports/${report.id}/regenerate-all-preview`,
        "POST",
        {},
      );
      preview = previewRes?.success || null;
    } catch {
      preview = null;
    } finally {
      hide();
    }

    Modal.confirm({
      title: t("ai_services.regenerate_all_confirm_title"),
      content: (
        <RegenerateAllConfirmContent preview={preview} loading={false} t={t} />
      ),
      okText: t("yes"),
      cancelText: t("no"),
      okButtonProps: {
        danger: true,
        disabled: preview && preview.sufficient === false && !preview.unlimited,
      },
      onOk: async () => {
        markReportRegenerating(report.id, true);
        try {
          const res = await apiRequest(
            `welcome/ai-services/reports/${report.id}/regenerate`,
            "POST",
            { mode: "full" },
          );
          if (res?.success) {
            message.success(t("ai_services.regenerate_all_started"));
            await refreshReportAfterRegen(report, res.success);
          } else if (res?.error === "INSUFFICIENT_AI_CREDITS") {
            message.error(t("ai_services.regenerate_all_insufficient_credits", {
              required: res.credits_required ?? 0,
              remaining: res.credits_remaining ?? 0,
            }));
          } else {
            message.error(
              typeof res?.error === "string" ? res.error : t("ai_services.save_error"),
            );
          }
        } catch {
          message.error(t("ai_services.save_error"));
        } finally {
          markReportRegenerating(report.id, false);
        }
      },
    });
  };
  const getMonthOptions = () => {
    const options = [];
    for (let i = 0; i < 12; i++) {
      const d = dayjs().subtract(i, "month");
      options.push({
        value: d.format("YYYY-MM"),
        label: d.format("MMMM YYYY"),
      });
    }
    return options;
  };

  const STATUS_BADGE_CFG = {
    ready:        { color: "rgba(82,196,26,1)",   bg: "rgba(82,196,26,0.12)",   border: "rgba(82,196,26,0.3)"   },
    processing:   { color: "rgba(250,140,22,1)",  bg: "rgba(250,140,22,0.12)",  border: "rgba(250,140,22,0.3)"  },
    provisioned:  { color: "rgba(108,71,213,1)",  bg: "rgba(108,71,213,0.12)",  border: "rgba(108,71,213,0.3)"  },
    finalizing:   { color: "rgba(24,144,255,1)",  bg: "rgba(24,144,255,0.12)",  border: "rgba(24,144,255,0.3)"  },
    partial:      { color: "rgba(250,140,22,1)",  bg: "rgba(250,140,22,0.12)",  border: "rgba(250,140,22,0.3)"  },
    insufficient_data: {
      color: "rgba(140,120,180,1)",
      bg: "rgba(140,120,180,0.12)",
      border: "rgba(140,120,180,0.3)",
    },
    failed:       { color: "rgba(255,77,79,1)",   bg: "rgba(255,77,79,0.12)",   border: "rgba(255,77,79,0.3)"   },
  };

  const IN_PROGRESS_STATUSES = new Set(["processing", "provisioned", "finalizing"]);

  const statusShowsSpinner = (status, report) => {
    if (status === "partial" || status === "insufficient_data") return false;
    if (IN_PROGRESS_STATUSES.has(status) && report && !reportHasActiveWidgetJobs(report)) {
      return false;
    }
    return IN_PROGRESS_STATUSES.has(status);
  };

  const reportDetailPath = (reportId) => `/console/ai-services/auto-reports/${reportId}`;

  const handleReportDetailNavigate = (e, reportId) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    history.push(reportDetailPath(reportId));
  };

  const renderStatusBadge = (status, { forceGenerating = false, report = null } = {}) => {
    if (forceGenerating) {
      const c = STATUS_BADGE_CFG.processing;
      return (
        <span
          className={styles.statusBadge}
          style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}
        >
          <LoadingOutlined style={{ marginRight: 4 }} />
          {t("ai_services.generating")}
        </span>
      );
    }
    const c = STATUS_BADGE_CFG[status] || STATUS_BADGE_CFG.processing;
    const showSpinner = statusShowsSpinner(status, report);
    return (
      <span
        className={styles.statusBadge}
        style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}
      >
        {showSpinner && <LoadingOutlined style={{ marginRight: 4 }} />}
        {status === "failed" && <WarningOutlined style={{ marginRight: 4 }} />}
        {t(`ai_services.status_${status}`)}
      </span>
    );
  };

  const getSortOptionLabel = (value) => {
    switch (value) {
      case "created_at:desc":
        return t("ai_services.sort_created_desc");
      case "created_at:asc":
        return t("ai_services.sort_created_asc");
      case "name:asc":
        return t("ai_services.sort_name_asc");
      case "name:desc":
        return t("ai_services.sort_name_desc");
      default:
        return t("ai_services.sort_created_desc");
    }
  };

  const renderReportActions = (report, { compact = false } = {}) => {
    const status = reportDisplayStatus(report);
    const isReady = status === "ready";
    const isPartial = status === "partial";
    const isInsufficientData = status === "insufficient_data";
    const isProcessing = PROCESSING_TAB_STATUSES.includes(status) && reportHasActiveWidgetJobs(report);
    const canOpenDetail = canOpenAiReportDetail(report);

    if (isReady || status === "finalizing" || status === "provisioned" || isPartial || isInsufficientData) {
      return (
        <>
          {canOpenDetail && (
            <Button
              size="small"
              icon={<FolderOpenOutlined />}
              href={reportDetailPath(report.id)}
              onClick={(e) => handleReportDetailNavigate(e, report.id)}
              className={styles.openBtn}
            >
              {!compact && t("ai_services.open_report")}
            </Button>
          )}
          <Button
            size="small"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadingId === report.id}
            onClick={() => handleDownload(report)}
            disabled={!report.pdf_url}
            className={styles.downloadBtn}
          >
            {!compact && t("ai_services.download_pdf")}
          </Button>
        </>
      );
    }

    if (isProcessing) {
      const canOpen = canOpenAiReportDetail(report);
      return (
        <>
          {canOpen && (
            <Button
              size="small"
              icon={<FolderOpenOutlined />}
              href={reportDetailPath(report.id)}
              onClick={(e) => handleReportDetailNavigate(e, report.id)}
              className={styles.openBtn}
            >
              {!compact && t("ai_services.open_report")}
            </Button>
          )}
          <span className={styles.processingLabel}>
            <LoadingOutlined /> {t("ai_services.generating")}
          </span>
        </>
      );
    }

    return (
      <span className={styles.failedLabel}>
        <WarningOutlined /> {t("ai_services.generation_failed")}
      </span>
    );
  };

  const renderReportTypeBadge = (report) => {
    if (isOneTimeAiReport(report)) {
      return (
        <span className={styles.reportTypeBadge}>
          {t("ai_services.report_type_one_time_short")}
        </span>
      );
    }
    return (
      <span className={`${styles.reportTypeBadge} ${styles.reportTypeBadgeScheduled}`}>
        {t("ai_services.report_type_scheduled_short")}
      </span>
    );
  };

  const renderReportListRow = (report) => {
    const status = reportDisplayStatus(report);
    const isReady = status === "ready";
    const canOpenDetail = canOpenAiReportDetail(report);

    return (
      <div key={report.id} className={styles.reportListRow}>
        <div className={`${styles.listCellName} ${!isReady ? styles.dimmed : ""}`}>
          {canOpenDetail ? (
            <a
              href={reportDetailPath(report.id)}
              className={styles.listNameLink}
              onClick={(e) => handleReportDetailNavigate(e, report.id)}
            >
              {report.brand_name}
            </a>
          ) : (
            report.brand_name
          )}
        </div>
        <div className={`${styles.listCellPeriod} ${!isReady ? styles.dimmed : ""}`}>
          <div>{formatAiReportPeriodLabel(report)}</div>
          {renderReportTypeBadge(report)}
        </div>
        <div className={styles.listCellStatus}>
          {renderStatusBadge(status, { report })}
        </div>
        <div className={`${styles.listCellReviews} ${!isReady ? styles.dimmed : ""}`}>
          {report.review_count != null
            ? formatAppNumber(report.review_count, i18n.language)
            : "—"}
        </div>
        <div className={`${styles.listCellWidgets} ${!isReady ? styles.dimmed : ""}`}>
          {report.widgets_total != null && report.widgets_total > 0
            ? formatWidgetBreakdownLabel(report, t)
            : "—"}
        </div>
        <div className={styles.listCellCreated}>
          {report.created_at
            ? formatInUserTimezone(
                report.created_at,
                userTimezoneLabel || userReducer,
                "D MMM YYYY, HH:mm",
              )
            : "—"}
        </div>
        <div className={styles.listCellActions}>
          <Button
            size="small"
            icon={<InfoCircleOutlined />}
            className={styles.infoBtn}
            onClick={() => setDetailReport(report)}
          />
          <Popconfirm
            title={t("ai_services.delete_confirm_title")}
            description={t("ai_services.delete_confirm_desc")}
            onConfirm={() => handleDelete(report)}
            okText={t("yes")}
            cancelText={t("no")}
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              loading={deletingId === report.id}
              className={styles.deleteBtn}
            />
          </Popconfirm>
          {renderReportActions(report, { compact: true })}
        </div>
      </div>
    );
  };

  const renderReportCard = (report) => {
    const status = reportDisplayStatus(report);
    const isReady = status === "ready";
    const isPartial = status === "partial";
    const isProcessing = PROCESSING_TAB_STATUSES.includes(status) && reportHasActiveWidgetJobs(report);

    const canOpenDetail = canOpenAiReportDetail(report);
    const isRegenerating = regeneratingReportIds.includes(report.id);
    const overflowMenuDisabled =
      isRegenerating || (IN_PROGRESS_STATUSES.has(status) && reportHasActiveWidgetJobs(report));

    return (
      <div
        key={report.id}
        className={`${styles.reportCard} ${canOpenDetail ? styles.reportCardClickable : ""}`}
      >
        {canOpenDetail && (
          <a
            href={reportDetailPath(report.id)}
            className={styles.cardLinkOverlay}
            onClick={(e) => handleReportDetailNavigate(e, report.id)}
            aria-label={t("ai_services.open_report")}
          />
        )}
        {/* Header: vendor name + status + utility actions */}
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <h3 className={`${styles.brandNameTitle} ${!isReady ? styles.dimmed : ""}`}>
              {report.brand_name}
            </h3>
          </div>
          <div className={`${styles.cardHeaderRight} ${styles.cardInteractive}`} onClick={(e) => e.stopPropagation()}>
            {renderStatusBadge(status, { forceGenerating: isRegenerating, report })}
            <Button
              size="small"
              icon={<InfoCircleOutlined />}
              className={styles.infoBtn}
              onClick={() => setDetailReport(report)}
            />
            <Popconfirm
              title={t("ai_services.delete_confirm_title")}
              description={t("ai_services.delete_confirm_desc")}
              onConfirm={() => handleDelete(report)}
              okText={t("yes")}
              cancelText={t("no")}
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                danger
                loading={deletingId === report.id}
                onClick={(e) => e.stopPropagation()}
                className={styles.deleteBtn}
              />
            </Popconfirm>
            <ReportCardOverflowMenu
              report={report}
              displayStatus={status}
              onRegeneratePdf={handleRegeneratePdf}
              onRepairWidgets={handleRepairWidgets}
              onRegenerateAll={handleRegenerateAll}
              disabled={overflowMenuDisabled}
            />
          </div>
        </div>

        {/* Date */}
        <p className={`${styles.reportDate} ${!isReady ? styles.dimmed : ""}`}>
          {formatAiReportPeriodLabel(report)}
        </p>
        {renderReportTypeBadge(report)}

        {/* Review count */}
        {report.review_count != null && (
          <p className={`${styles.reviewCount} ${!isReady ? styles.dimmed : ""}`}>
            {t("ai_services.review_count_label", { count: formatAppNumber(report.review_count, i18n.language) })}
          </p>
        )}

        {/* Metric chips */}
        {(report.metric_ids || []).length > 0 && (
          <div className={styles.metricChips}>
            {(report.metric_ids || []).map((mid) =>
              renderMetricStatusChip(report, mid, t, {
                forceIncompleteInProgress: isRegenerating,
              }),
            )}
          </div>
        )}

        {report.widgets_total != null && report.widgets_total > 0 && (
          <p className={`${styles.widgetsProgress} ${!isReady ? styles.dimmed : ""}`}>
            {formatWidgetBreakdownLabel(report, t)}
          </p>
        )}

        {/* Created at */}
        {report.created_at && (
          <p className={styles.cardDate}>
            {t("ai_services.created_at_label")}:{" "}
            {formatInUserTimezone(
              report.created_at,
              userTimezoneLabel || userReducer,
              "D MMM YYYY, HH:mm",
            )}
          </p>
        )}

        {/* Processing bar — shown for all in-progress statuses */}
        {isProcessing && (
          <div className={styles.processingBar}>
            <div
              className={
                report.widgets_total > 0
                  ? styles.processingBarFillDeterminate
                  : styles.processingBarFill
              }
              style={
                report.widgets_total > 0
                  ? {
                      width: `${Math.min(
                        100,
                        Math.round(
                          (100 * (report.widgets_ready ?? 0)) / report.widgets_total,
                        ),
                      )}%`,
                    }
                  : undefined
              }
            />
          </div>
        )}

        {/* Error */}
        {report.error_message && (
          <p className={styles.errorMessage}>{report.error_message}</p>
        )}

        {/* Footer actions */}
        <div className={`${styles.cardFooter} ${styles.cardInteractive}`} onClick={(e) => e.stopPropagation()}>
          {renderReportActions(report)}
        </div>
      </div>
    );
  };


  const tabItems = STATUS_TABS.map((tab) => ({
    key: tab,
    label: t(`ai_services.tab_${tab}`),
  }));

  /* ── Detail popup helpers ── */
  const coverageLabel = (report) => formatAiReportCoverageLabel(report);

  const buildDetailFooter = (report) => {
    const btns = [];
    if (canOpenAiReportDetail(report)) {
      btns.push(
        <Button
          key="open"
          icon={<FolderOpenOutlined />}
          onClick={() => { setDetailReport(null); history.push(`/console/ai-services/auto-reports/${report.id}`); }}
        >
          {t("ai_services.open_report")}
        </Button>
      );
    }
    if (report.pdf_url) {
      btns.push(
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => window.open(report.pdf_url, "_blank")}
          className={styles.downloadBtn}
        >
          {t("ai_services.download_pdf")}
        </Button>
      );
    }
    return btns;
  };

  if (pageView === "settings") {
    return (
      <div className={styles.pageViewTabsWrap}>
        <div className={styles.tabsRow}>
          <Tabs
            activeKey="settings"
            onChange={handlePageViewChange}
            items={pageViewTabItems}
            className={styles.reportTabs}
          />
        </div>
        <AiAutoRefreshSettings />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Report / Settings switch — same route, panel-within-page */}
      <div className={styles.tabsRow}>
        <Tabs
          activeKey="reports"
          onChange={handlePageViewChange}
          items={pageViewTabItems}
          className={styles.reportTabs}
        />
      </div>

      {/* Header */}
      <div className={styles.pageHeader}>
        <p className={styles.pageTitle}>{t("ai_services.reports_title")}</p>
      </div>

      {/* Tabs */}
      <div className={styles.tabsRow}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={tabItems}
          className={styles.reportTabs}
        />
      </div>

      {/* Filters */}
      <div className={styles.filtersRow}>
        <div
          className={styles.reportTypePill}
          role="group"
          aria-label={t("ai_services.report_type_filter_label")}
        >
          <button
            type="button"
            className={`${styles.reportTypePillOption} ${
              allReportTypeTab === "all" ? styles.reportTypePillOptionActive : ""
            }`}
            onClick={() => setAllReportTypeTab("all")}
            aria-pressed={allReportTypeTab === "all"}
          >
            {t("ai_services.report_type_all_short")}
          </button>
          <button
            type="button"
            className={`${styles.reportTypePillOption} ${
              allReportTypeTab === "monthly" ? styles.reportTypePillOptionActive : ""
            }`}
            onClick={() => setAllReportTypeTab("monthly")}
            aria-pressed={allReportTypeTab === "monthly"}
          >
            {t("ai_services.report_type_scheduled_short")}
          </button>
          <button
            type="button"
            className={`${styles.reportTypePillOption} ${
              allReportTypeTab === "one_time" ? styles.reportTypePillOptionActive : ""
            }`}
            onClick={() => setAllReportTypeTab("one_time")}
            aria-pressed={allReportTypeTab === "one_time"}
          >
            {t("ai_services.report_type_one_time_short")}
          </button>
        </div>
        <Select
          placeholder={t("ai_services.filter_month")}
          allowClear
          style={{ width: 160 }}
          value={monthFilter || undefined}
          onChange={(v) => setMonthFilter(v || "")}
        >
          {getMonthOptions().map((o) => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Select>
          <Input
            prefix={<SearchOutlined />}
            placeholder={t("ai_services.search_vendors")}
            value={brandSearchInput}
            onChange={(e) => setBrandSearchInput(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchReports(true)}
          loading={loading}
        >
          {t("ai_services.refresh")}
        </Button>
        <div className={styles.filtersSpacer} />
        <Select
          value={sortValue}
          onChange={setSortValue}
          style={{ width: 240 }}
          aria-label={t("ai_services.sort_label")}
        >
          {SORT_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {getSortOptionLabel(option.value)}
            </Option>
          ))}
        </Select>
        <div className={styles.viewToggle} role="group">
          <button
            type="button"
            className={`${styles.viewToggleBtn} ${viewMode === "list" ? styles.viewToggleBtnActive : ""}`}
            onClick={() => setViewMode("list")}
            aria-label={t("ai_services.view_list")}
            aria-pressed={viewMode === "list"}
          >
            <UnorderedListOutlined />
          </button>
          <button
            type="button"
            className={`${styles.viewToggleBtn} ${viewMode === "card" ? styles.viewToggleBtnActive : ""}`}
            onClick={() => setViewMode("card")}
            aria-label={t("ai_services.view_card")}
            aria-pressed={viewMode === "card"}
          >
            <AppstoreOutlined />
          </button>
        </div>
      </div>

      {/* Tab switching indicator — subtle, keeps existing cards visible */}
      {tabLoading && (
        <div className={styles.tabLoadingBar}>
          <Spin size="small" />
        </div>
      )}

      {/* ── Detail Modal ── */}
      {detailReport && (
        <Modal
          open={!!detailReport}
          onCancel={() => setDetailReport(null)}
          footer={buildDetailFooter(detailReport)}
          title={
            <div className={styles.modalTitle}>
              <span>{detailReport.brand_name}</span>
              {renderStatusBadge(reportDisplayStatus(detailReport), { report: detailReport })}
            </div>
          }
          width={480}
        >
          <div className={styles.detailGrid}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {t("ai_services.reports_detail_dashboard_label")}
              </span>
              <span
                className={styles.detailValue}
                title={reportDashboardContextTitle(
                  detailReport,
                  sessionDashboardName,
                  t,
                )}
              >
                {reportDashboardContextPrimary(detailReport, sessionDashboardName) ||
                  "—"}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("ai_services.report_month")}</span>
              <span className={styles.detailValue}>
                {detailReport.report_month ? dayjs(detailReport.report_month).format("MMMM YYYY") : "—"}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("ai_services.coverage_period")}</span>
              <span className={styles.detailValue}>{coverageLabel(detailReport)}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("ai_services.created_at_label")}</span>
              <span className={styles.detailValue}>
                {detailReport.created_at
                  ? formatInUserTimezone(
                      detailReport.created_at,
                      detailReport.user_timezone_label ||
                        userTimezoneLabel ||
                        userReducer,
                      "D MMMM YYYY HH:mm",
                    )
                  : "—"}
              </span>
            </div>
            {(detailReport.metric_ids || []).length > 0 && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("ai_services.metrics")}</span>
                <div className={styles.metricChips}>
                  {(detailReport.metric_ids || []).map((mid) =>
                    renderMetricStatusChip(detailReport, mid, t, {
                      forceIncompleteInProgress: regeneratingReportIds.includes(
                        detailReport.id,
                      ),
                    }),
                  )}
                </div>
              </div>
            )}
            {detailReport.widgets_total != null && detailReport.widgets_total > 0 && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("ai_services.widgets_progress_label")}</span>
                <span className={styles.detailValue}>
                  {formatWidgetBreakdownLabel(detailReport, t)}
                </span>
              </div>
            )}
            {detailReport.pivot_value && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("ai_services.vendor")}</span>
                <span className={styles.detailValue}>{detailReport.pivot_value}</span>
              </div>
            )}
            {detailReport.review_count != null && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{t("ai_services.review_count_label_short")}</span>
                <span className={styles.detailValue}>
                  {formatAppNumber(detailReport.review_count, i18n.language)}
                </span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Report Grid */}
      {loading && reports.length === 0 ? (
        <div className={styles.loadingWrapper}>
          <Spin size="large" />
        </div>
      ) : reports.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <InboxOutlined />
          </div>
          <div className={styles.emptyStateText}>
            <h3 className={styles.emptyStateTitle}>{t("ai_services.no_reports")}</h3>
            <p className={styles.emptyStateDesc}>{t("ai_services.no_reports_desc")}</p>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => history.push("/console/ai-services/auto-reports-settings")}
            className={styles.goToSettingsBtn}
          >
            {t("ai_services.go_to_settings")}
          </Button>
        </div>
      ) : (
        <>
          {viewMode === "card" ? (
            <div className={styles.reportGrid}>
              {reports.map(renderReportCard)}
            </div>
          ) : (
            <div className={styles.reportList}>
              <div className={styles.reportListHeader}>
                <div className={styles.listCellName}>{t("ai_services.list_col_name")}</div>
                <div className={styles.listCellPeriod}>{t("ai_services.list_col_period")}</div>
                <div className={styles.listCellStatus}>{t("ai_services.list_col_status")}</div>
                <div className={styles.listCellReviews}>{t("ai_services.list_col_reviews")}</div>
                <div className={styles.listCellWidgets}>{t("ai_services.list_col_widgets")}</div>
                <div className={styles.listCellCreated}>{t("ai_services.list_col_created")}</div>
                <div className={styles.listCellActions}>{t("ai_services.list_col_actions")}</div>
              </div>
              {reports.map(renderReportListRow)}
            </div>
          )}

          <div className={styles.paginationRow}>
            <span className={styles.showingLabel}>
              {t("ai_services.showing")} {reports.length} {t("ai_services.of")} {total} {t("ai_services.reports_label")}
            </span>
            {hasMore && (
              <Button onClick={handleLoadMore} loading={loading}>
                {t("ai_services.load_more")}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AiReportsPage;
